/**
 * zonedCalendar — calendar-grid arithmetic in the viewer's DISPLAY zone.
 *
 * Before #676 the calendar placed events by browser-local time and only
 * LABELLED them in the display zone (#540 deliberately left "grid math
 * untouched"). A Denver-profile user on a Manila browser therefore got
 * Manila-bucketed cells carrying Denver labels. Everything here places and
 * labels in one zone, so the two can no longer disagree.
 *
 * The unit of navigation is a DAY KEY: a 'YYYY-MM-DD' wall date in the display
 * zone. Day keys are plain strings and their arithmetic is done on UTC dates,
 * so nothing in this module ever depends on the browser's zone. Instants
 * (event start/end, fetch windows) cross the boundary only through
 * `dayKeyOf` / `minutesOf` (instant → zone wall clock) and `instantAt`
 * (zone wall clock → instant).
 *
 * All-day events are the exception, and deliberately so: they are CALENDAR
 * DATES, not instants. Google's `date` and Graph's `isAllDay` both arrive as
 * UTC midnight, and zone-shifting them is exactly how a holiday lands on the
 * previous day west of UTC. `allDayRange` reads their UTC date parts and never
 * converts.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { parseInstant, zoneOffsetMinutes, formatGmtOffset } from '../timezone';

export const MINUTES_PER_DAY = 24 * 60;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function isDayKey(value) {
    return typeof value === 'string' && DAY_KEY_RE.test(value);
}

function keyToUtcDate(dayKey) {
    const m = DAY_KEY_RE.exec(dayKey);
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function utcDateToKey(d) {
    return d.toISOString().slice(0, 10);
}

/** Parts of a day key, for labels. month is 0-based. */
export function dayKeyParts(dayKey) {
    const d = keyToUtcDate(dayKey);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth(), day: d.getUTCDate(), weekday: d.getUTCDay() };
}

export function addDaysKey(dayKey, n) {
    return utcDateToKey(new Date(keyToUtcDate(dayKey).getTime() + n * DAY_MS));
}

export function addMonthsKey(dayKey, n) {
    const { year, month } = dayKeyParts(dayKey);
    // Anchor on the 1st so Jan 31 + 1 month is Feb 1, not Mar 3.
    return utcDateToKey(new Date(Date.UTC(year, month + n, 1)));
}

/** Whole days from `a` to `b` (b − a). */
export function daysBetween(a, b) {
    return Math.round((keyToUtcDate(b) - keyToUtcDate(a)) / DAY_MS);
}

/** Sunday-start week containing `dayKey`, as 7 day keys. */
export function weekDaysOf(dayKey) {
    const start = addDaysKey(dayKey, -dayKeyParts(dayKey).weekday);
    return Array.from({ length: 7 }, (_, i) => addDaysKey(start, i));
}

/** The 42-cell (6-week, Sunday-start) grid for the month containing `dayKey`. */
export function monthCells(dayKey) {
    const { year, month } = dayKeyParts(dayKey);
    const first = utcDateToKey(new Date(Date.UTC(year, month, 1)));
    const start = addDaysKey(first, -dayKeyParts(first).weekday);
    return Array.from({ length: 42 }, (_, i) => addDaysKey(start, i));
}

export function isSameMonth(a, b) {
    const pa = dayKeyParts(a);
    const pb = dayKeyParts(b);
    return pa.year === pb.year && pa.month === pb.month;
}

/** Today's day key in `zone`. */
export function todayKey(zone, now = new Date()) {
    return formatInTimeZone(now, zone, 'yyyy-MM-dd');
}

/** The zone wall date of an instant. '' when the instant is unparseable. */
export function dayKeyOf(instant, zone) {
    const d = parseInstant(instant);
    return d ? formatInTimeZone(d, zone, 'yyyy-MM-dd') : '';
}

/** Minutes since zone-midnight of an instant's wall clock. */
export function minutesOf(instant, zone) {
    const d = parseInstant(instant);
    if (!d) return 0;
    const [h, m] = formatInTimeZone(d, zone, 'H:m').split(':').map(Number);
    return h * 60 + m;
}

/**
 * The instant at `minutes` past midnight of `dayKey` in `zone`. Minutes past
 * the end of the day roll into the next day key, so `instantAt(k, 1440)` is
 * the start of the following day.
 */
export function instantAt(dayKey, minutes, zone) {
    const carry = Math.floor(minutes / MINUTES_PER_DAY);
    const key = carry ? addDaysKey(dayKey, carry) : dayKey;
    const rem = minutes - carry * MINUTES_PER_DAY;
    const hh = String(Math.floor(rem / 60)).padStart(2, '0');
    const mm = String(rem % 60).padStart(2, '0');
    return fromZonedTime(`${key}T${hh}:${mm}:00`, zone);
}

/**
 * The fetch window for a view, as UTC ISO strings for useCalendarEvents. It
 * covers every cell the view draws — in month view that includes the leading
 * and trailing days of the neighbouring months.
 */
export function rangeFor(view, anchorDay, zone) {
    let first;
    let days;
    if (view === 'day') {
        first = anchorDay;
        days = 1;
    } else if (view === 'month') {
        first = monthCells(anchorDay)[0];
        days = 42;
    } else {
        first = weekDaysOf(anchorDay)[0];
        days = 7;
    }
    return {
        fromIso: instantAt(first, 0, zone).toISOString(),
        toIso: instantAt(addDaysKey(first, days), 0, zone).toISOString(),
    };
}

/** Step the anchor one period in `direction` (±1) for the given view. */
export function stepAnchor(view, anchorDay, direction) {
    if (view === 'month') return addMonthsKey(anchorDay, direction);
    return addDaysKey(anchorDay, direction * (view === 'day' ? 1 : 7));
}

/**
 * The toolbar's period label (prototype `calRangeLabel`):
 *   week, one month   → "13–19 September 2026"
 *   week, two months  → "28 Sep – 4 Oct 2026"
 *   day               → "Thursday, 17 September 2026"
 *   month             → "September 2026"
 */
export function rangeLabel(view, anchorDay) {
    const a = dayKeyParts(anchorDay);
    if (view === 'month') return `${MONTH_NAMES[a.month]} ${a.year}`;
    if (view === 'day') return `${WEEKDAY_NAMES[a.weekday]}, ${a.day} ${MONTH_NAMES[a.month]} ${a.year}`;
    const days = weekDaysOf(anchorDay);
    const s = dayKeyParts(days[0]);
    const e = dayKeyParts(days[6]);
    if (s.month === e.month) return `${s.day}–${e.day} ${MONTH_NAMES[e.month]} ${e.year}`;
    const short = (p) => `${p.day} ${MONTH_NAMES[p.month].slice(0, 3)}`;
    return `${short(s)} – ${short(e)} ${e.year}`;
}

/** "GMT+8" / "GMT+5:30" — the zone's current offset, via the shared formatter. */
export function zoneOffsetLabel(zone, at = new Date()) {
    return formatGmtOffset(zoneOffsetMinutes(zone, at)).replace(/[()]/g, '');
}

// ─── All-day events: calendar dates, never zone-shifted ──────────────────────

function utcDateKeyOf(instant) {
    const d = parseInstant(instant);
    return d ? utcDateToKey(d) : '';
}

/**
 * True for an event the grid draws in the all-day band: a real all-day event,
 * or a timed one lasting 24 hours or more (a multi-day conference).
 */
export function isAllDayLike(event) {
    if (event?.all_day) return true;
    const s = parseInstant(event?.start);
    const e = parseInstant(event?.end);
    return Boolean(s && e && e - s >= DAY_MS);
}

/**
 * [startKey, endKeyExclusive) of a band event. Real all-day events read their
 * UTC date parts (see the module note); long timed events use the zone.
 */
export function allDayRange(event, zone) {
    let start;
    let end;
    if (event?.all_day) {
        start = utcDateKeyOf(event.start);
        end = utcDateKeyOf(event.end);
    } else {
        start = dayKeyOf(event.start, zone);
        // A timed event ending exactly at midnight does not occupy that day.
        const endInstant = parseInstant(event.end);
        end = endInstant ? dayKeyOf(new Date(endInstant.getTime() - 1), zone) : '';
        end = end ? addDaysKey(end, 1) : '';
    }
    if (!start) return null;
    if (!end || daysBetween(start, end) < 1) end = addDaysKey(start, 1);
    return [start, end];
}

/**
 * The day key an event is ON: its first calendar date when all-day, else its
 * start's wall date in `zone`. Anything that names or navigates to an event's
 * day uses this — zone-formatting an all-day start moves it a day west of UTC.
 */
export function eventDayKey(event, zone) {
    if (event?.all_day) return allDayRange(event, zone)?.[0] || '';
    return dayKeyOf(event?.start, zone);
}

/** "Friday, 2 October 2026" for a day key. */
export function longDayLabel(dayKey) {
    const p = dayKeyParts(dayKey);
    return `${WEEKDAY_NAMES[p.weekday]}, ${p.day} ${MONTH_NAMES[p.month]} ${p.year}`;
}

/** Does a band event cover `dayKey`? */
export function coversDay(range, dayKey) {
    return Boolean(range) && range[0] <= dayKey && dayKey < range[1];
}
